/*
 * The header's behaviour: the store pill's words, the bag's count (Shopify's lines and the hub's), the mega menus'
 * aria-expanded, the Menu sheet below 1200px, and the points in the signed-in pill (from the hub; "You" until then,
 * never 0).
 */
import { storeChoice, loadCart, hub } from "./ms-hub.js";

/** "Pick-up · Rosebery", "Delivery · Rosebery", or "Pick a store". */
export function storeLabel(store, fulfilment = "pickup") {
  if (!store?.name) return "Pick a store";
  return `${fulfilment === "delivery" ? "Delivery" : "Pick-up"} · ${store.name}`;
}

/** Everything in the bag: Shopify's cart (merch, drops, gift cards) and the hub's held lines. */
export function bagCount(shopifyCart, hubCart) {
  const hubUnits = (hubCart?.lines || []).filter((l) => !l.lapsed).reduce((n, l) => n + (Number(l.quantity) || 1), 0);
  return (shopifyCart?.item_count || 0) + hubUnits;
}

/** The store pill's link: the Order page, asked to open its store sheet over the store you have. */
export function pickHref(href) {
  const h = href || "/pages/order";
  if (/[?&]change=store(?:&|#|$)/.test(h)) return h;
  const [path, hash = ""] = h.split("#");
  return `${path}${path.includes("?") ? "&" : "?"}change=store${hash ? "#" + hash : ""}`;
}

/**
 * Before launch the store sends /pages/specials to the old specials site (a redirect; the page itself is unpublished), so
 * a preview of this theme (<html data-preview>) opens the board through the contact page's specials view. Returns the
 * preview's address for a link to the board, or null for any other link (flavour pages included).
 */
export function previewHref(href) {
  const to = "/pages/contact?view=specials";
  const h = href || "";
  // The old specials site, anywhere on it, goes to the new page.
  if (/^https?:\/\/specials\.gelatomessina\.com(?:[/?#]|$)/.test(h)) return to;
  // A sign-in that would come back to /pages/specials comes back to the new page instead.
  const back = /^(\/customer_authentication\/login\?(?:.*&)?return_to=)(%2Fpages%2Fspecials)((?:&|#|$).*)$/i.exec(h);
  if (back) return back[1] + encodeURIComponent(to) + back[3];
  const m = /^(?:https?:\/\/(?:www\.)?gelatomessina\.com)?\/pages\/specials(\?[^#]*)?(#.*)?$/.exec(h);
  if (!m) return null;
  return to + (m[1] ? "&" + m[1].slice(1) : "") + (m[2] || "");
}

function previewLinks() {
  if (!document.documentElement.dataset.preview) return;
  const fix = (a) => { const to = a && previewHref(a.getAttribute("href")); if (to) a.setAttribute("href", to); };
  document.querySelectorAll('a[href*="/pages/specials"], a[href*="specials.gelatomessina.com"], a[href*="return_to=%2Fpages%2Fspecials"]').forEach(fix);
  // Links drawn later (the cabinet, the Order page) are fixed as they're clicked.
  document.addEventListener("click", (e) => fix(e.target.closest?.("a[href]")), true);
}

function paintStore() {
  const label = storeLabel(storeChoice(), loadCart().fulfilment || "pickup");
  document.querySelectorAll("[data-store-label]").forEach((el) => { el.textContent = label; });
}

async function paintBag() {
  let shopify = null;
  try { const r = await fetch("/cart.js", { headers: { accept: "application/json" } }); if (r.ok) shopify = await r.json(); } catch {}
  const n = bagCount(shopify, loadCart());
  document.querySelectorAll("[data-bag]").forEach((bag) => {
    let c = bag.querySelector(".count");
    if (!n) { c?.remove(); bag.setAttribute("aria-label", "Your order"); return; }
    if (!c) { c = document.createElement("span"); c.className = "count"; bag.append(c); }
    c.textContent = String(n);
    bag.setAttribute("aria-label", `Your order, ${n} item${n === 1 ? "" : "s"}`);
  });
}

async function paintPoints() {
  const el = document.querySelector("[data-me-points]");
  if (!el) return;
  const r = await hub("me", {});
  const balance = r.ok ? r.data?.points?.balance : null;
  if (typeof balance === "number") { el.textContent = balance.toLocaleString("en-AU"); el.closest(".me-pill")?.setAttribute("aria-label", `You, ${balance} points`); }
}

function menuSheet() {
  const sheet = document.getElementById("site-menu");
  const toggles = document.querySelectorAll('[aria-controls="site-menu"]');
  if (!sheet) return;
  const set = (open) => {
    sheet.hidden = !open;
    toggles.forEach((t) => t.setAttribute("aria-expanded", String(open)));
    document.documentElement.classList.toggle("sheet-open", open);
    // While it's open the page behind can't take focus; Close takes it first.
    for (const n of document.querySelectorAll("body > *")) if (n !== sheet && !n.contains(sheet) && n.tagName !== "SCRIPT") n.inert = open;
    if (open) sheet.querySelector(".sm-close")?.focus(); else document.querySelector(".hd-menu")?.focus();
  };
  toggles.forEach((t) => t.addEventListener("click", () => set(sheet.hidden)));
  // A link to a place on this page (Book a tour, Book a table) closes the menu so you land there.
  sheet.addEventListener("click", (e) => { if (e.target.closest('a[href^="#"]')) set(false); });
  sheet.addEventListener("keydown", (e) => {
    if (e.key === "Escape") { set(false); return; }
    if (e.key !== "Tab") return;
    const all = [...sheet.querySelectorAll("a[href], button:not([disabled]), input, [tabindex='0']")].filter((n) => n.offsetParent !== null);
    if (!all.length) return;
    const first = all[0], last = all[all.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
}

/**
 * Hover timing for the mega menus (a mouse only): a menu opens after a short pause, so sweeping across the nav doesn't
 * flash every menu; it waits before closing, so a slow move down into it never loses it; moving across to another item
 * swaps them after a shorter pause (a diagonal move down that crosses the next item doesn't switch), with "swap" set so
 * the new one fades in instead of bouncing. Only the first drop bounces.
 */
export function megaHover({ openDelay = 60, closeDelay = 280, swapDelay = 90 } = {}) {
  let current = null, openT = null, closeT = null;
  const stop = (t) => { if (t) clearTimeout(t); return null; };
  const show = (ni, swap) => { ni.classList.add("hov"); ni.classList.toggle("swap", swap); current = ni; };
  const hide = (ni) => { ni?.classList.remove("hov", "swap"); if (current === ni) current = null; };
  return {
    enter(ni) {
      closeT = stop(closeT); openT = stop(openT);
      if (ni === current) return;
      if (current) { const was = current; openT = setTimeout(() => { hide(was); show(ni, true); }, swapDelay); }
      else openT = setTimeout(() => show(ni, false), openDelay);
    },
    leave(ni) {
      if (ni !== current) { openT = stop(openT); return; }
      closeT = stop(closeT);
      closeT = setTimeout(() => hide(ni), closeDelay);
    },
    close() { openT = stop(openT); closeT = stop(closeT); hide(current); },
  };
}

/** Mega menus: hover opens them for a mouse (megaHover); for a keyboard each top item is a link, and its toggle button opens it. */
function megaMenus() {
  const hv = megaHover();
  document.querySelectorAll(".navgrp > .ni").forEach((ni) => {
    if (ni.querySelector(":scope > .mega")) {
      ni.addEventListener("pointerenter", (e) => { if (e.pointerType === "mouse") hv.enter(ni); });
      ni.addEventListener("pointerleave", (e) => { if (e.pointerType === "mouse") hv.leave(ni); });
    }
    const tog = ni.querySelector(":scope > .ni-tog");
    if (!tog) return;
    const set = (v) => { tog.setAttribute("aria-expanded", String(v)); ni.classList.toggle("open", v); };
    tog.addEventListener("click", () => { ni.classList.remove("shut"); set(tog.getAttribute("aria-expanded") !== "true"); });
    ni.addEventListener("focusout", (e) => { if (!ni.contains(e.relatedTarget)) set(false); });
    // Escape closes it, opened by the toggle or by hover; a hovered one stays shut until the mouse leaves.
    ni.addEventListener("keydown", (e) => { if (e.key === "Escape") { set(false); hv.close(); ni.classList.add("shut"); tog.focus(); } });
    ni.addEventListener("mouseleave", () => ni.classList.remove("shut"));
  });
}

if (typeof document !== "undefined") {
  const boot = () => {
    previewLinks(); paintStore(); paintBag(); paintPoints(); menuSheet(); megaMenus();
    document.addEventListener("ms:store", paintStore);
    document.addEventListener("ms:cart", () => { paintStore(); paintBag(); });
    // A Shopify add (merch posted, a drop) says ms:shop-cart; the hub cart says ms:count. Either repaints the bag.
    for (const ev of ["ms:shopify-cart", "ms:shop-cart", "ms:count"]) document.addEventListener(ev, paintBag);
    // The store pill's chevron means change the store. On the Order page it opens the store sheet rather than reloading
    // the page; anywhere else it lands on the Order page with the sheet open. Both pills: the bar's and the phone menu's.
    document.querySelectorAll("[data-store-pill]").forEach((pill) => {
      pill.href = pickHref(pill.getAttribute("href"));
      // Where it opens the sheet it is a button, and says so (Space works too).
      if (document.getElementById("order-stores")) {
        pill.setAttribute("role", "button"); pill.setAttribute("aria-haspopup", "dialog"); pill.setAttribute("aria-controls", "order-stores");
        pill.addEventListener("keydown", (e) => { if (e.key === " ") { e.preventDefault(); pill.click(); } });
      }
      pill.addEventListener("click", (e) => {
        const sheet = document.getElementById("order-stores");
        if (sheet && sheet.open) {
          e.preventDefault();
          // From inside the phone menu, the menu closes first.
          if (pill.closest("#site-menu")) document.querySelector("#site-menu .sm-close")?.click();
          sheet.open(e.currentTarget);
        }
      });
    });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
}
